import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { OrderDto, OrderItemDto } from './types';
import {
  ShoppingCart,
  ChevronDown,
  MapPin,
  Calendar,
  Package,
  Loader2,
  Key,
  Wrench,
  Compass,
  FileCheck2,
  FileText,
  Activity,
  Plane
} from "lucide-react";

const Label = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
  <span className={`text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2 block ml-1 ${className}`}>
    {children}
  </span>
);

const GlassCard = ({ children, className = "", onClick }: any) => (
  <div
    onClick={onClick}
    className={`bg-[#0F172A]/80 backdrop-blur-2xl border border-slate-800/80 rounded-3xl p-6 shadow-2xl transition-all duration-300 flex flex-col relative overflow-hidden ${className}`}
  >
    <div className="flex-1 z-10">{children}</div>
  </div>
);

const MyOrders: React.FC = () => {
  const { myOrders, isAppDataLoading, getDictValue } = useAuth();
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>({});
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const orders: OrderDto[] = myOrders || [];

  const toggleOrder = (orderId: string) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    return new Intl.DateTimeFormat('ro-RO', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(timestamp));
  };

  const getStatusBadge = (status?: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('paid') || s.includes('success') || s.includes('completed')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-mono font-bold uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          {getDictValue("orders.order.status.paid")}
        </span>
      );
    }
    if (s.includes('pending') || s.includes('process')) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-[10px] font-mono font-bold uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
          {getDictValue("orders.order.status.pending")}
        </span>
      );
    }
    if (s.includes('cancelled')) {
        return (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-[10px] font-mono font-bold uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                  {getDictValue("orders.order.status.cancelled")}
                </span>
              );
          }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[10px] font-mono font-bold uppercase tracking-wider">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
        {getDictValue("orders.order.status.failed")}
      </span>
    );
  };

  const getProductIcon = (type?: string) => {
    const t = type?.toUpperCase() || '';
    if (t.includes('SALE') || t.includes('AIRCRAFT')) return <Plane size={16} className="text-orange-400" />;
    if (t.includes('RENTAL')) return <Key size={16} className="text-blue-400" />;
    if (t.includes('PART')) return <Wrench size={16} className="text-emerald-400" />;
    return <Compass size={16} className="text-purple-400" />;
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined) return '€0.00';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
  };

  if (isAppDataLoading && orders.length === 0) {
    return (
      <div className="min-h-screen bg-[#020617] flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-orange-500/5 blur-[120px] rounded-full pointer-events-none" />
        <Loader2 className="animate-spin text-orange-500 mb-4 h-10 w-10" />
        <p className="text-slate-500 font-mono text-xs tracking-widest uppercase">
          {getDictValue("orders.loading")}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 pb-20 font-sans selection:bg-orange-500/30 selection:text-white relative">
      {/* Glow ambiental de fundal */}
      <div className="absolute top-0 right-10 w-96 h-96 bg-orange-500/5 blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-600/5 blur-[150px] rounded-full pointer-events-none" />

      <div className="container max-w-7xl py-12 px-6 relative z-10">

        {/* HEADER */}
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-l-4 border-orange-500 pl-6">
          <div>
            <p className="text-orange-400 font-mono text-xs tracking-[0.3em] mb-1">
              {getDictValue("orders.header")}
            </p>
            <h1 className="text-5xl font-black text-white tracking-tight italic">
              {getDictValue("orders.title").split(" ")[0]}{" "}
              <span className="text-slate-500">{getDictValue("orders.title").split(" ").slice(1).join(" ")}</span>
            </h1>
          </div>
          <div className="flex items-center gap-4 bg-slate-900/50 p-3 rounded-2xl border border-slate-800/80">
            <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center border border-orange-500/20">
               <ShoppingCart className="text-orange-400" size={20} />
            </div>
            <div className="text-right px-2">
                <p className="text-[9px] text-slate-500 uppercase font-black tracking-widest leading-none">
                  {getDictValue("orders.count.label")}
                </p>
                <p className="text-emerald-400 font-mono text-xs font-bold mt-1 flex items-center justify-end gap-1.5">
                   <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                   {orders.length}
                </p>
            </div>
          </div>
        </header>

        {orders.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-slate-800 rounded-3xl bg-slate-900/10">
            <Package className="mx-auto text-slate-700 mb-4" size={48} />
            <p className="text-slate-500 font-mono text-xs uppercase tracking-widest">
              {getDictValue("orders.no.orders")}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const isExpanded = expandedOrders[order.orderId || ''] || false;
              return (
                <div key={order.orderId} className="group">

                  {/* Card Principal */}
                  <GlassCard
                    onClick={() => order.orderId && toggleOrder(order.orderId)}
                    className={`cursor-pointer hover:border-orange-500/40 hover:bg-[#1E293B]/20 active:scale-[0.995] ${
                      isExpanded ? 'border-orange-500/40 bg-[#0c1222]' : 'bg-[#0F172A]/50'
                    }`}
                  >
                    {isExpanded && (
                      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-orange-500 to-blue-600" />
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">

                      {/* ID Comandă */}
                      <div className="md:col-span-3 flex flex-col gap-1">
                        <span className="text-[8px] text-slate-500 font-mono uppercase tracking-[0.2em]">
                          {getDictValue("orders.order.id.label")}
                        </span>
                        <span className="font-mono text-sm font-black text-orange-400 group-hover:text-white transition-colors truncate tracking-wider">
                          #{order.orderId || 'N/A'}
                        </span>
                      </div>

                      {/* Data Plasării */}
                      <div className="md:col-span-3 flex flex-col gap-1">
                        <span className="text-[8px] text-slate-500 font-mono uppercase tracking-[0.2em]">
                          {getDictValue("orders.order.date.label")}
                        </span>
                        <span className="text-xs font-mono text-slate-300 flex items-center gap-1.5">
                          <Calendar size={13} className="text-orange-500/80 shrink-0" />
                          {formatDate(order.orderDate)}
                        </span>
                      </div>

                      {/* Status Plată */}
                      <div className="md:col-span-2 flex flex-col gap-1 items-start">
                        <span className="text-[8px] text-slate-500 font-mono uppercase tracking-[0.2em]">
                          {getDictValue("orders.order.status.label")}
                        </span>
                        {getStatusBadge(order.paymentStatus)}
                      </div>

                      {/* Volum Articole */}
                      <div className="md:col-span-1 flex flex-col gap-1">
                        <span className="text-[8px] text-slate-500 font-mono uppercase tracking-[0.2em]">
                          {getDictValue("orders.order.quantity.label")}
                        </span>
                        <span className="text-xs text-slate-300 font-mono font-bold">
                          {order.orderItems?.length || 0} {getDictValue("orders.order.quantity.txt")}
                        </span>
                      </div>

                      {/* Valoare Brută */}
                      <div className="md:col-span-2 flex flex-col gap-1">
                        <span className="text-[8px] text-slate-500 font-mono uppercase tracking-[0.2em]">
                          {getDictValue("orders.order.total.label")}
                        </span>
                        <span className="font-mono text-base font-black text-white tracking-tight">
                          {formatCurrency(order.grossValue)}
                        </span>
                      </div>

                      {/* Indicator Expandare */}
                      <div className="md:col-span-1 flex justify-end">
                        <div className={`p-2 rounded-xl bg-slate-950 border border-slate-800/80 text-slate-500 transition-all duration-300 ${isExpanded ? 'rotate-180 border-orange-500 text-orange-400 bg-orange-500/10' : 'group-hover:text-white group-hover:border-slate-700'}`}>
                          <ChevronDown size={14} />
                        </div>
                      </div>

                    </div>
                  </GlassCard>

                  {/* Detalii Expandate */}
                  {isExpanded && (
                    <div className="mt-3 mx-2 bg-[#080d19]/95 backdrop-blur-md border border-slate-800/80 rounded-3xl p-6 space-y-8 animate-in slide-in-from-top-3 duration-300">

                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-6 border-b border-slate-800/40">

                        {/* Adresa */}
                        <div className="lg:col-span-7 flex flex-col">
                          <Label>{getDictValue("orders.order.billing.address.label")}</Label>
                          <div className="flex gap-3 bg-slate-950/40 border border-slate-800/60 p-4 rounded-2xl flex-1 items-start">
                            <MapPin className="text-orange-500 shrink-0 mt-0.5" size={14} />
                            <p className="text-xs text-slate-300 font-mono leading-relaxed">
                              {order.billingAddress || 'Nu s-a specificat o adresă de facturare.'}
                            </p>
                          </div>
                        </div>

                        {/* Defalcare Prețuri */}
                        <div className="lg:col-span-5 flex flex-col">
                          <Label>{getDictValue("orders.order.billing.pricing.label")}</Label>
                          <div className="bg-slate-950/40 border border-slate-800/60 p-4 rounded-2xl space-y-2.5 text-xs font-mono text-slate-400">
                            <div className="flex justify-between items-center">
                              <span>{getDictValue("orders.order.billing.price.net.label")}</span>
                              <span className="text-slate-300 font-bold">{formatCurrency(order.netValue)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span>{getDictValue("orders.order.billing.price.vat.label")}</span>
                              <span className="text-slate-300 font-bold">{formatCurrency(order.vatValue)}</span>
                            </div>
                            <div className="h-px bg-slate-800/50 my-2" />
                            <div className="flex justify-between items-center font-black text-white bg-orange-500/5 p-2 px-3 rounded-xl border border-orange-500/10">
                              <span className="text-[10px] uppercase tracking-wider text-orange-400 flex items-center gap-1.5 font-bold">
                                <FileText size={12} /> {getDictValue("orders.order.billing.price.total.label")}
                              </span>
                              <span className="text-base text-emerald-400 font-bold">{formatCurrency(order.grossValue)}</span>
                            </div>
                          </div>
                        </div>

                      </div>

                      {/* Articole Comandate */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 pl-1">
                          <FileCheck2 size={14} className="text-orange-500" />
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em]">
                            {getDictValue("orders.order.items.title")}
                          </span>
                        </div>

                        {(!order.orderItems || order.orderItems.length === 0) ? (
                          <div className="p-6 rounded-2xl bg-slate-950/20 border border-slate-800 text-center">
                            <p className="text-xs text-slate-500 italic">{getDictValue("orders.order.no.items")}</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {order.orderItems.map((item: OrderItemDto, idx) => {
                              const imgKey = `${order.orderId}-${item.id || idx}`;
                              const hasError = imageErrors[imgKey];
                              const resolvedImgUrl = item.mediaUrl;

                              return (
                                <div
                                  key={item.id || idx}
                                  className="group/item bg-[#0b101d] border border-slate-800/40 hover:border-orange-500/30 rounded-2xl p-4 flex flex-col lg:flex-row items-center justify-between gap-6 transition-all duration-200"
                                >
                                  <div className="flex items-center gap-5 w-full lg:w-auto">
                                    <div className="w-20 h-20 rounded-xl bg-slate-950 border border-slate-800/60 overflow-hidden shrink-0 flex items-center justify-center relative transition-colors group-hover/item:border-orange-500/40">
                                      {resolvedImgUrl && !hasError ? (
                                        <img
                                          src={resolvedImgUrl}
                                          alt={item.productName || 'Asset'}
                                          className="w-full h-full object-cover relative z-10 transition-transform duration-300 group-hover/item:scale-105"
                                          onError={() => {
                                            setImageErrors(prev => ({ ...prev, [imgKey]: true }));
                                          }}
                                        />
                                      ) : null}

                                      <div className={`absolute inset-0 flex items-center justify-center bg-gradient-to-b from-slate-950 to-slate-900 transition-opacity duration-300 ${(!resolvedImgUrl || hasError) ? 'opacity-100 z-20' : 'opacity-0 z-0'}`}>
                                        <div className="w-10 h-10 rounded-lg bg-orange-500/5 border border-orange-500/10 flex items-center justify-center">
                                          {getProductIcon(item.productType)}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex-1">
                                      <h4 className="font-bold text-white text-base tracking-tight font-sans group-hover/item:text-orange-400 transition-colors">
                                        {item.productName}
                                      </h4>
                                      <div className="mt-2 flex flex-wrap items-center gap-2">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-slate-950 border border-slate-800 text-[8px] tracking-widest uppercase font-black text-slate-400 font-mono">
                                          {getProductIcon(item.productType)}
                                          {item.productType || 'ASSET'}
                                        </span>
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-orange-500/5 border border-orange-500/10 text-[9px] font-mono text-orange-400">
                                          {getDictValue("orders.order.items.quantity.label")}{" "}
                                          <strong className="font-black ml-0.5">{item.quantity || 0}</strong>
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Grid Financiar */}
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-8 w-full lg:w-auto text-right font-mono text-xs border-t lg:border-t-0 border-slate-800/40 pt-4 lg:pt-0">
                                    <div>
                                      <span className="block text-[8px] text-slate-500 uppercase tracking-wider mb-1 font-sans">
                                        {getDictValue("orders.order.items.net.label")}
                                      </span>
                                      <span className="text-slate-400 font-medium">{formatCurrency(item.netPrice)}</span>
                                    </div>
                                    <div>
                                      <span className="block text-[8px] text-slate-500 uppercase tracking-wider mb-1 font-sans">
                                        {getDictValue("orders.order.items.vat.rate.label")}
                                      </span>
                                      <span className="text-slate-500">{item.vatRate ? `${item.vatRate}%` : '0%'}</span>
                                    </div>
                                    <div>
                                      <span className="block text-[8px] text-slate-500 uppercase tracking-wider mb-1 font-sans">
                                        {getDictValue("orders.order.items.vat.label")}
                                      </span>
                                      <span className="text-slate-400 font-medium">{formatCurrency(item.vatValue)}</span>
                                    </div>
                                    <div className="bg-slate-950/30 p-1.5 rounded-xl border border-slate-800/40 px-3 flex flex-col justify-center min-w-[100px]">
                                      <span className="block text-[8px] text-orange-400 uppercase tracking-wider mb-1 font-sans">
                                        {getDictValue("orders.order.items.total.label")}
                                      </span>
                                      <span className="text-xs font-black text-white">{formatCurrency(item.grossPrice)}</span>
                                    </div>
                                  </div>

                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
};

export default MyOrders;